import { Injectable, inject } from '@angular/core';

import type { UserDateFormat, UserPreferences, UserTimeFormat } from '../models/auth.models';
import { AuthSessionService } from './auth-session.service';

export interface UserDateTimePreferences {
  dateFormat: UserDateFormat;
  timeFormat: UserTimeFormat;
  timeZone: string;
}

export interface DateTimeFormatOptions {
  includeTimeZoneName?: boolean;
}

interface ZonedDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

interface DateParts {
  year: number;
  month: number;
  day: number;
}

export const USER_DATE_FORMAT_OPTIONS = [
  'MMM dd, yyyy',
  'dd MMM, yyyy',
  'yyyy, MMM dd',
  'dd/MM/yyyy',
  'MM/dd/yyyy',
  'yyyy/MM/dd',
  'dd-MM-yyyy',
  'MM-dd-yyyy',
  'yyyy-MM-dd',
  'dd-MMM-yyyy',
  'MMM-dd-yyyy',
  'dd.MM.yyyy',
  'dd.MMM.yyyy',
] as const satisfies readonly UserDateFormat[];

export const USER_TIME_FORMAT_OPTIONS = [
  'hh:mm tt',
  'h:mm tt',
  'HH:mm',
  'H:mm',
  'hh:mm:ss tt',
  'h:mm:ss tt',
  'HH:mm:ss',
  'H:mm:ss',
] as const satisfies readonly UserTimeFormat[];

export const FIELD_TIME_FORMAT_OPTIONS = ['None', ...USER_TIME_FORMAT_OPTIONS] as const;

export const DEFAULT_USER_DATE_TIME_PREFERENCES: UserDateTimePreferences = {
  dateFormat: 'MM/dd/yyyy',
  timeFormat: 'hh:mm tt',
  timeZone: 'America/Chicago',
};

const LEGACY_TIME_FORMAT_ALIASES: Record<string, UserTimeFormat> = {
  'hh:mm a': 'hh:mm tt',
  'h:mm a': 'h:mm tt',
};

const DATE_FORMATS = new Set<string>(USER_DATE_FORMAT_OPTIONS);
const TIME_FORMATS = new Set<string>(USER_TIME_FORMAT_OPTIONS);
const SHORT_MONTH_TO_NUMBER: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

export const normalizeUserDateTimePreferences = (
  input?: Partial<UserDateTimePreferences> | Partial<UserPreferences> | null,
): UserDateTimePreferences => {
  const rawTimeFormat = (input?.timeFormat ?? '') as string;
  const mappedTimeFormat = LEGACY_TIME_FORMAT_ALIASES[rawTimeFormat] ?? rawTimeFormat;

  return {
    dateFormat: DATE_FORMATS.has(input?.dateFormat ?? '')
      ? (input?.dateFormat as UserDateFormat)
      : DEFAULT_USER_DATE_TIME_PREFERENCES.dateFormat,
    timeFormat: TIME_FORMATS.has(mappedTimeFormat)
      ? (mappedTimeFormat as UserTimeFormat)
      : DEFAULT_USER_DATE_TIME_PREFERENCES.timeFormat,
    timeZone:
      typeof input?.timeZone === 'string' && input.timeZone.trim().length > 0
        ? input.timeZone
        : DEFAULT_USER_DATE_TIME_PREFERENCES.timeZone,
  };
};

const getParts = (date: Date, timeZone: string, options: Intl.DateTimeFormatOptions): Record<string, string> => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    ...options,
  }).formatToParts(date);

  return parts.reduce<Record<string, string>>((accumulator, part) => {
    if (part.type !== 'literal') {
      accumulator[part.type] = part.value;
    }
    return accumulator;
  }, {});
};

const getZonedDateTimeParts = (date: Date, timeZone: string): ZonedDateTimeParts => {
  const parts = getParts(date, timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  return {
    year: Number(parts['year'] ?? 0),
    month: Number(parts['month'] ?? 0),
    day: Number(parts['day'] ?? 0),
    hour: Number(parts['hour'] ?? 0),
    minute: Number(parts['minute'] ?? 0),
    second: Number(parts['second'] ?? 0),
  };
};

const getTimeZoneOffsetMs = (date: Date, timeZone: string): number => {
  const parts = getZonedDateTimeParts(date, timeZone);
  const utcTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return utcTimestamp - date.getTime();
};

const formatInputDateTimeParts = (parts: Pick<ZonedDateTimeParts, 'year' | 'month' | 'day' | 'hour' | 'minute'>): string => {
  const year = String(parts.year).padStart(4, '0');
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  const hour = String(parts.hour).padStart(2, '0');
  const minute = String(parts.minute).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const formatInputDateParts = (parts: DateParts): string => {
  const year = String(parts.year).padStart(4, '0');
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isValidDateParts = (parts: DateParts): boolean => {
  if (
    !Number.isInteger(parts.year) ||
    !Number.isInteger(parts.month) ||
    !Number.isInteger(parts.day) ||
    parts.month < 1 ||
    parts.month > 12 ||
    parts.day < 1 ||
    parts.day > 31
  ) {
    return false;
  }

  const parsed = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  return (
    parsed.getUTCFullYear() === parts.year &&
    parsed.getUTCMonth() === parts.month - 1 &&
    parsed.getUTCDate() === parts.day
  );
};

const parseShortMonth = (value: string): number | null => {
  return SHORT_MONTH_TO_NUMBER[value.trim().toLowerCase()] ?? null;
};

const parseFormattedDateParts = (value: string, format: UserDateFormat): DateParts | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const shortMonthPattern = '([A-Za-z]{3})';
  const numericDatePattern = '(\\d{1,4})';
  const matchGroups = (pattern: RegExp, map: (match: RegExpMatchArray) => DateParts | null): DateParts | null => {
    const match = trimmed.match(pattern);
    return match ? map(match) : null;
  };

  switch (format) {
    case 'MMM dd, yyyy':
      return matchGroups(new RegExp(`^${shortMonthPattern} (\\d{2}), (\\d{4})$`, 'i'), (match) => {
        const month = parseShortMonth(match[1] ?? '');
        return month ? { year: Number(match[3]), month, day: Number(match[2]) } : null;
      });
    case 'dd MMM, yyyy':
      return matchGroups(new RegExp(`^(\\d{2}) ${shortMonthPattern}, (\\d{4})$`, 'i'), (match) => {
        const month = parseShortMonth(match[2] ?? '');
        return month ? { year: Number(match[3]), month, day: Number(match[1]) } : null;
      });
    case 'yyyy, MMM dd':
      return matchGroups(new RegExp(`^(\\d{4}), ${shortMonthPattern} (\\d{2})$`, 'i'), (match) => {
        const month = parseShortMonth(match[2] ?? '');
        return month ? { year: Number(match[1]), month, day: Number(match[3]) } : null;
      });
    case 'dd/MM/yyyy':
      return matchGroups(/^(\d{2})\/(\d{2})\/(\d{4})$/, (match) => ({
        year: Number(match[3]),
        month: Number(match[2]),
        day: Number(match[1]),
      }));
    case 'MM/dd/yyyy':
      return matchGroups(/^(\d{2})\/(\d{2})\/(\d{4})$/, (match) => ({
        year: Number(match[3]),
        month: Number(match[1]),
        day: Number(match[2]),
      }));
    case 'yyyy/MM/dd':
      return matchGroups(/^(\d{4})\/(\d{2})\/(\d{2})$/, (match) => ({
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      }));
    case 'dd-MM-yyyy':
      return matchGroups(/^(\d{2})-(\d{2})-(\d{4})$/, (match) => ({
        year: Number(match[3]),
        month: Number(match[2]),
        day: Number(match[1]),
      }));
    case 'MM-dd-yyyy':
      return matchGroups(/^(\d{2})-(\d{2})-(\d{4})$/, (match) => ({
        year: Number(match[3]),
        month: Number(match[1]),
        day: Number(match[2]),
      }));
    case 'yyyy-MM-dd':
      return matchGroups(/^(\d{4})-(\d{2})-(\d{2})$/, (match) => ({
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      }));
    case 'dd-MMM-yyyy':
      return matchGroups(new RegExp(`^(\\d{2})-${shortMonthPattern}-(\\d{4})$`, 'i'), (match) => {
        const month = parseShortMonth(match[2] ?? '');
        return month ? { year: Number(match[3]), month, day: Number(match[1]) } : null;
      });
    case 'MMM-dd-yyyy':
      return matchGroups(new RegExp(`^${shortMonthPattern}-(\\d{2})-(\\d{4})$`, 'i'), (match) => {
        const month = parseShortMonth(match[1] ?? '');
        return month ? { year: Number(match[3]), month, day: Number(match[2]) } : null;
      });
    case 'dd.MM.yyyy':
      return matchGroups(/^(\d{2})\.(\d{2})\.(\d{4})$/, (match) => ({
        year: Number(match[3]),
        month: Number(match[2]),
        day: Number(match[1]),
      }));
    case 'dd.MMM.yyyy':
      return matchGroups(new RegExp(`^(\\d{2})\\.${shortMonthPattern}\\.(\\d{4})$`, 'i'), (match) => {
        const month = parseShortMonth(match[2] ?? '');
        return month ? { year: Number(match[3]), month, day: Number(match[1]) } : null;
      });
    default:
      return matchGroups(new RegExp(`^${numericDatePattern}$`), () => null);
  }
};

@Injectable({ providedIn: 'root' })
export class UserDateTimeFormatService {
  private readonly authSession = inject(AuthSessionService);

  preferences(): UserDateTimePreferences {
    return normalizeUserDateTimePreferences(this.authSession.currentUser()?.preferences);
  }

  formatDateWithPreferences(
    value: string | number | Date,
    input?: Partial<UserDateTimePreferences> | Partial<UserPreferences> | null,
  ): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    const preferences = normalizeUserDateTimePreferences(input);
    return this.formatDateWithResolvedPreferences(date, preferences);
  }

  formatDate(value: string | number | Date): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    return this.formatDateWithResolvedPreferences(date, this.preferences());
  }

  formatTimeWithPreferences(
    value: string | number | Date,
    input?: Partial<UserDateTimePreferences> | Partial<UserPreferences> | null,
  ): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    const preferences = normalizeUserDateTimePreferences(input);
    return this.formatTimeWithResolvedPreferences(date, preferences);
  }

  formatTime(value: string | number | Date): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    return this.formatTimeWithResolvedPreferences(date, this.preferences());
  }

  formatDateTimeWithPreferences(
    value: string | number | Date,
    input?: Partial<UserDateTimePreferences> | Partial<UserPreferences> | null,
    options: DateTimeFormatOptions = {},
  ): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    const preferences = normalizeUserDateTimePreferences(input);
    return this.formatDateTimeWithResolvedPreferences(date, preferences, options);
  }

  formatDateTime(value: string | number | Date, options: DateTimeFormatOptions = {}): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    return this.formatDateTimeWithResolvedPreferences(date, this.preferences(), options);
  }

  formatMonthYear(value: string | number | Date): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    const preferences = this.preferences();
    const parts = getParts(date, preferences.timeZone, {
      month: 'long',
      year: 'numeric',
    });

    return `${parts['month']} ${parts['year']}`.trim();
  }

  formatMonthName(value: string | number | Date): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    const preferences = this.preferences();
    return getParts(date, preferences.timeZone, { month: 'long' })['month'] ?? '';
  }

  formatMonthDayTime(value: string | number | Date): string {
    const date = this.parseDate(value);
    if (!date) {
      return String(value);
    }

    const preferences = this.preferences();
    const month = getParts(date, preferences.timeZone, { month: 'short' })['month'] ?? '';
    const day = getParts(date, preferences.timeZone, { day: 'numeric' })['day'] ?? '';
    const timeText = this.formatTime(date);

    return `${month} ${day}, ${timeText}`;
  }

  timeZoneLabel(style: 'short' | 'long', value: string | number | Date = new Date()): string {
    const date = this.parseDate(value);
    if (!date) {
      return style === 'long' ? this.preferences().timeZone : '';
    }

    const preferences = this.preferences();
    const zoneName = getParts(date, preferences.timeZone, { timeZoneName: style })['timeZoneName'];

    return zoneName ?? preferences.timeZone;
  }

  dateFieldDefaults(): Pick<UserDateTimePreferences, 'dateFormat' | 'timeFormat'> {
    const preferences = this.preferences();
    return {
      dateFormat: preferences.dateFormat,
      timeFormat: preferences.timeFormat,
    };
  }

  formatDateTimeInput(value: string | number | Date): string {
    const date = this.parseDate(value);
    if (!date) {
      return '';
    }

    const preferences = this.preferences();
    return formatInputDateTimeParts(getZonedDateTimeParts(date, preferences.timeZone));
  }

  formatDateInput(value: string | number | Date): string {
    const date = this.parseDate(value);
    if (!date) {
      return '';
    }

    const preferences = this.preferences();
    const parts = getZonedDateTimeParts(date, preferences.timeZone);
    return formatInputDateParts({
      year: parts.year,
      month: parts.month,
      day: parts.day,
    });
  }

  parseDateInput(value: string): Date | null {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const parts = {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
    };

    if (!isValidDateParts(parts)) {
      return null;
    }

    return this.resolveZonedDateTime(parts.year, parts.month, parts.day, 0, 0);
  }

  parseFormattedDate(value: string): Date | null {
    const preferences = this.preferences();
    const parts = parseFormattedDateParts(value, preferences.dateFormat);
    if (!parts || !isValidDateParts(parts)) {
      return null;
    }

    return this.resolveZonedDateTime(parts.year, parts.month, parts.day, 0, 0);
  }

  parseDateTimeInput(value: string): Date | null {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const [, yearText, monthText, dayText, hourText, minuteText] = match;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const hour = Number(hourText);
    const minute = Number(minuteText);

    if (!isValidDateParts({ year, month, day }) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return null;
    }

    return this.resolveZonedDateTime(year, month, day, hour, minute);
  }

  private parseDate(value: string | number | Date): Date | null {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatDateWithResolvedPreferences(date: Date, preferences: UserDateTimePreferences): string {
    const dateParts = getParts(date, preferences.timeZone, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const shortMonth = getParts(date, preferences.timeZone, { month: 'short' })['month'] ?? '';

    switch (preferences.dateFormat) {
      case 'MMM dd, yyyy':
        return `${shortMonth} ${dateParts['day']}, ${dateParts['year']}`;
      case 'dd MMM, yyyy':
        return `${dateParts['day']} ${shortMonth}, ${dateParts['year']}`;
      case 'yyyy, MMM dd':
        return `${dateParts['year']}, ${shortMonth} ${dateParts['day']}`;
      case 'dd/MM/yyyy':
        return `${dateParts['day']}/${dateParts['month']}/${dateParts['year']}`;
      case 'MM/dd/yyyy':
        return `${dateParts['month']}/${dateParts['day']}/${dateParts['year']}`;
      case 'yyyy/MM/dd':
        return `${dateParts['year']}/${dateParts['month']}/${dateParts['day']}`;
      case 'dd-MM-yyyy':
        return `${dateParts['day']}-${dateParts['month']}-${dateParts['year']}`;
      case 'MM-dd-yyyy':
        return `${dateParts['month']}-${dateParts['day']}-${dateParts['year']}`;
      case 'yyyy-MM-dd':
        return `${dateParts['year']}-${dateParts['month']}-${dateParts['day']}`;
      case 'dd-MMM-yyyy':
        return `${dateParts['day']}-${shortMonth}-${dateParts['year']}`;
      case 'MMM-dd-yyyy':
        return `${shortMonth}-${dateParts['day']}-${dateParts['year']}`;
      case 'dd.MM.yyyy':
        return `${dateParts['day']}.${dateParts['month']}.${dateParts['year']}`;
      case 'dd.MMM.yyyy':
        return `${dateParts['day']}.${shortMonth}.${dateParts['year']}`;
      default:
        return `${dateParts['month']}/${dateParts['day']}/${dateParts['year']}`;
    }
  }

  private formatTimeWithResolvedPreferences(date: Date, preferences: UserDateTimePreferences): string {
    const isTwelveHour = preferences.timeFormat.includes('tt');
    const includeSeconds = preferences.timeFormat.includes(':ss');
    const hour = preferences.timeFormat.startsWith('hh') || preferences.timeFormat.startsWith('HH') ? '2-digit' : 'numeric';
    const parts = getParts(date, preferences.timeZone, {
      hour,
      minute: '2-digit',
      second: includeSeconds ? '2-digit' : undefined,
      hour12: isTwelveHour,
    });

    const base = includeSeconds
      ? `${parts['hour']}:${parts['minute']}:${parts['second']}`
      : `${parts['hour']}:${parts['minute']}`;

    return isTwelveHour ? `${base} ${parts['dayPeriod']}` : base;
  }

  private formatDateTimeWithResolvedPreferences(
    date: Date,
    preferences: UserDateTimePreferences,
    options: DateTimeFormatOptions,
  ): string {
    const dateText = this.formatDateWithResolvedPreferences(date, preferences);
    const timeText = this.formatTimeWithResolvedPreferences(date, preferences);

    if (!options.includeTimeZoneName) {
      return `${dateText}, ${timeText}`;
    }

    const zoneName = getParts(date, preferences.timeZone, {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    })['timeZoneName'];

    return zoneName ? `${dateText}, ${timeText} ${zoneName}` : `${dateText}, ${timeText}`;
  }

  private resolveZonedDateTime(year: number, month: number, day: number, hour: number, minute: number): Date | null {
    const preferences = this.preferences();
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    let offsetMs = getTimeZoneOffsetMs(new Date(utcGuess), preferences.timeZone);
    let resolved = new Date(utcGuess - offsetMs);

    const refinedOffsetMs = getTimeZoneOffsetMs(resolved, preferences.timeZone);
    if (refinedOffsetMs !== offsetMs) {
      offsetMs = refinedOffsetMs;
      resolved = new Date(utcGuess - offsetMs);
    }

    const parts = getZonedDateTimeParts(resolved, preferences.timeZone);
    if (
      parts.year !== year ||
      parts.month !== month ||
      parts.day !== day ||
      parts.hour !== hour ||
      parts.minute !== minute
    ) {
      return null;
    }

    return resolved;
  }
}
